/**
 * Pure helpers behind the installable app (PWA): which platform's install
 * steps to show, when to show the install and notification banners, and
 * whether push notifications can be turned on. Kept free of browser globals
 * so they can be unit tested; app/hooks/usePwa.ts feeds them the real values.
 */

export type Platform = "ios" | "android" | "desktop";

/**
 * iPadOS reports a desktop Mac user agent, so a "Macintosh" with a touch
 * screen is an iPad.
 */
export function detectPlatform(
  userAgent: string,
  maxTouchPoints: number,
): Platform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
}

/**
 * In-app browsers (Instagram, Facebook, LinkedIn, Gmail...) cannot add a
 * site to the home screen; the guide asks to open the page in the real
 * browser first.
 */
export function isInAppBrowser(userAgent: string): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|Twitter|GSA\/|; wv\)/i.test(
    userAgent,
  );
}

/** Days a dismissed banner stays hidden. */
export const BANNER_SNOOZE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

function snoozed(dismissedAt: number | null, now: number): boolean {
  return dismissedAt !== null && now - dismissedAt < BANNER_SNOOZE_DAYS * DAY_MS;
}

/**
 * The install banner is for phones and tablets that are not already running
 * the installed app, unless the user dismissed it recently.
 */
export function shouldShowInstallBanner({
  platform,
  standalone,
  dismissedAt,
  now,
}: {
  platform: Platform;
  standalone: boolean;
  dismissedAt: number | null;
  now: number;
}): boolean {
  if (platform === "desktop" || standalone) return false;
  return !snoozed(dismissedAt, now);
}

export type PushState =
  /** VAPID key missing on the server: the feature is switched off. */
  | "unavailable"
  /** The browser has no Push API at all. */
  | "unsupported"
  /** iOS only delivers push to the app installed on the home screen. */
  | "needs-install"
  /** The user blocked notifications; only the browser settings can undo it. */
  | "denied"
  /** Can be turned on (or is on). */
  | "available";

export function pushState({
  configured,
  platform,
  standalone,
  hasServiceWorker,
  hasPushManager,
  hasNotification,
  permission,
}: {
  configured: boolean;
  platform: Platform;
  standalone: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  permission: NotificationPermission | null;
}): PushState {
  if (!configured) return "unavailable";
  // Safari on iOS only exposes the Push API inside the installed app, so a
  // missing PushManager in a Safari tab means "install first", not "never".
  if (platform === "ios" && !standalone) return "needs-install";
  if (!hasServiceWorker || !hasPushManager || !hasNotification) {
    return "unsupported";
  }
  if (permission === "denied") return "denied";
  return "available";
}

/**
 * Inside the installed app, nudge users who have not decided about
 * notifications yet. Never shown once they allowed or blocked them.
 */
export function shouldShowPushBanner({
  standalone,
  state,
  permission,
  subscribed,
  dismissedAt,
  now,
}: {
  standalone: boolean;
  state: PushState;
  permission: NotificationPermission | null;
  subscribed: boolean;
  dismissedAt: number | null;
  now: number;
}): boolean {
  if (!standalone || state !== "available") return false;
  if (permission !== "default" || subscribed) return false;
  return !snoozed(dismissedAt, now);
}

/** Decodes a base64url VAPID key into the bytes PushManager.subscribe wants. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/**
 * Whether a subscription was made with the given VAPID key. After a key
 * rotation the browser keeps the old subscription, which the server can no
 * longer send to, so it has to be replaced.
 */
export function subscriptionMatchesKey(
  subscriptionKey: ArrayBuffer | null | undefined,
  vapidPublicKey: string,
): boolean {
  if (!subscriptionKey) return true; // Browser does not expose it; assume ok.
  const expected = urlBase64ToUint8Array(vapidPublicKey);
  const actual = new Uint8Array(subscriptionKey);
  if (actual.length !== expected.length) return false;
  return actual.every((byte, i) => byte === expected[i]);
}

/** Reads a "dismissed at" timestamp; storage can be blocked or corrupted. */
export function parseDismissedAt(raw: string | null): number | null {
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Where the installed app's back button goes. The installed app has no
 * browser back button (iOS has no back gesture there either), so the header
 * shows one: back through the app's own history, or to the board when the
 * page was opened directly (a notification tap, the app launch). `null`
 * hides it: in a browser tab, and on the board with nothing to go back to.
 */
export function backButtonTarget({
  standalone,
  isFirstEntry,
  pathname,
}: {
  standalone: boolean;
  /** This page is the first entry of the app's history (no in-app back). */
  isFirstEntry: boolean;
  pathname: string;
}): "history" | "/jobs" | null {
  if (!standalone) return null;
  if (!isFirstEntry) return "history";
  return pathname === "/jobs" ? null : "/jobs";
}
